/* eslint-disable function-paren-newline */

import { jwtDecode } from 'jwt-decode';

import { generateImageURL } from '../../../../utils';

import {
  ILoginTokenJWTPayload,
  ILoginUserData,
  ILoginUserDataRaw,
} from '../types';

/**
 * TODO:: Move this to utilities
 * @param string - The string to be capitalized
 * @returns The capitalized string
 */
function capitalizeFirstLetter(string: string) {
  if (!string) return '';

  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * It will format raw response data of login mutation.
 * TODO: Remove hardcoded condition for permission
 */
export function formatUserDetails(
  userDetails: ILoginUserDataRaw
): ILoginUserData {
  const {
    bidActivationMode: biddingType,
    storeOptions,
    token,
    user,
    ...rest
  } = userDetails || {};

  const IBDAA_READ_APP_PERMISSION = [
    'VIEW_INBOUND',
    'PENDENCIES',
    'LIST_POD_INVENTORY',
    'VIEW_INVENTORY',
    'LIST_BLACK_BOX',
  ];

  const IBDAA_WRITE_APP_PERMISSION = [
    'ADD_TO_TRANSACTION',
    'ADD_TO_BLACK_BOX',
    'NEW_INBOUND',
  ];

  /**
   * Permission to create new trays
   */
  const IBDAA_CREATE_NEW_TRAY_PERMISSION = 'CREATE_TRAY';

  /**
   * Permission to create new transfer
   */
  const IBDAA_NEW_TRANSFER_PERMISSION = 'INV_TRANSFER';

  /**
   * Permission to update inventory
   */
  const IBDAA_UPDATE_INVENTORY_PERMISSION = 'INVENTORY_EDIT';
  /**
   * Permission to switch POD
   */
  const IBDAA_POD_SWITCH_PERMISSION = 'POD_SWITCH';

  /**
   * Permission to view Grade Review Requests
   */
  const DEALER_VIEW_GRR_PERMISSION = 'VIEW_GRR';

  /**
   * Permission to be a member of Grade Review Requests
   */
  const DEALER_MEMBER_GRR_PERMISSION = 'MEMBER_GRR';

  /**
   * Permission to view, update profile and to change password
   */
  const PROFILE_PERMISSION = 'OWN_ACCOUNT';

  /**
   * Permission to view deals list and deal details
   */
  const DEALER_VIEW_DEALS_PERMISSION = 'VIEW_APPRAISAL';

  /**
   * Permission to view transactions list and transaction details
   */
  const DEALER_VIEW_TRANSACTIONS_PERMISSION = 'VIEW_STORE_ACCOUNT_STMT';

  /**
   * Permission to view bids list and bid details
   */
  const DEALER_VIEW_BIDS_PERMISSION = 'VIEW_ASSET';

  /**
   * Permission to create a new bid
   */
  const DEALER_CREATE_SINGLE_BID_PERMISSION = 'UPDATE_BID';

  /**
   * Permission to create new trays
   */
  const DEALER_REGISTRATION_PERMISSION = 'DEALER_REGISTRATION';

  /**
   * Permission to update bids
   */
  const DEALER_CREATE_BIDS_PERMISSION = 'CREATE_BID';

  /**
   * Permission to view own account notifications
   */
  const DEALER_VIEW_NOTIFICATIONS_PERMISSION = 'VIEW_NOTIFICATIONS';

  /**
   * Permission to change password
   */
  const DEALER_CHANGE_PASSWORD_PERMISSION = 'CHANGE_PASSWORD';

  /**
   * Permission to edit profile
   */
  const DEALER_EDIT_PROFILE_PERMISSION = 'UPDATE_PROFILE';

  /**
   * Permission to see assessment images
   */
  const DEALER_VIEW_ASSESSMENT_IMAGES_PERMISSION = 'CAN_VIEW_ASSESSMENT_IMAGES';
  /**
   * Permission to view account statement for buyers
   */
  const DEALER_VIEW_ACCOUNT_STATEMENT = 'DEALER_VIEW_ACCOUNT_STATEMENT';

  const decodedJwt = jwtDecode<ILoginTokenJWTPayload>(token);
  const permissions = decodedJwt.permissions || [];

  // check if user has all the read permissions
  const isReadPermission = IBDAA_READ_APP_PERMISSION.every((permission) =>
    permissions.includes(permission)
  );

  // check if user has all the write permissions
  const isWritePermission = IBDAA_WRITE_APP_PERMISSION.every((permission) =>
    permissions.includes(permission)
  );

  // check if user has create new trays permission
  const isCreateTrayPermission = permissions.includes(
    IBDAA_CREATE_NEW_TRAY_PERMISSION
  );

  // check if user has new transfer permission
  const isNewTransferPermission = permissions.includes(
    IBDAA_NEW_TRANSFER_PERMISSION
  );

  // check if user has update inventory permission
  const hasUpdateInventoryPermission = permissions.includes(
    IBDAA_UPDATE_INVENTORY_PERMISSION
  );

  const hasPodSwitchPermission = permissions.includes(
    IBDAA_POD_SWITCH_PERMISSION
  );

  // check if user has view grade review request permission
  const hasViewGrrPermission = permissions.includes(DEALER_VIEW_GRR_PERMISSION);

  // check if user has member grade review request permission
  const hasMemberGrrPermission = permissions.includes(
    DEALER_MEMBER_GRR_PERMISSION
  );

  // Check if user has own account permission
  const hasProfilePermission = permissions.includes(PROFILE_PERMISSION);

  const hasDealerRegistrationPermission =
    permissions.indexOf(DEALER_REGISTRATION_PERMISSION) !== -1;

  const hasViewDealsPermission = permissions.includes(
    DEALER_VIEW_DEALS_PERMISSION
  );

  const hasViewTransactionsPermission = permissions.includes(
    DEALER_VIEW_TRANSACTIONS_PERMISSION
  );

  // check if user has view bids permission
  const hasViewBidsPermission = permissions.includes(
    DEALER_VIEW_BIDS_PERMISSION
  );

  // If both create and update bids permissions are present,
  // then the user has create single bid permission
  const hasCreateBidsPermission =
    permissions.includes(DEALER_CREATE_SINGLE_BID_PERMISSION) &&
    permissions.includes(DEALER_CREATE_BIDS_PERMISSION);

  // check if user has view notifications permission
  const hasViewNotificationsPermission = permissions.includes(
    DEALER_VIEW_NOTIFICATIONS_PERMISSION
  );

  // check if user has change password permission
  const hasChangePasswordPermission = permissions.includes(
    DEALER_CHANGE_PASSWORD_PERMISSION
  );

  // check if user has edit profile permission
  const hasEditProfilePermission = permissions.includes(
    DEALER_EDIT_PROFILE_PERMISSION
  );

  // check if user has view assessment images permission
  const hasViewAssessmentImagesPermission = permissions.includes(
    DEALER_VIEW_ASSESSMENT_IMAGES_PERMISSION
  );

  // check if user has view account statement permission
  const hasViewAccountStatementPermission = permissions.includes(
    DEALER_VIEW_ACCOUNT_STATEMENT
  );

  const {
    firstName: firstNameRaw,
    lastName: lastNameRaw,
    authenticationType,
    userId,
    tenantId,
    ...userRest
  } = user || {};

  const firstName = capitalizeFirstLetter(firstNameRaw);
  const lastName = capitalizeFirstLetter(lastNameRaw);
  const fullName = `${firstName || ''} ${lastName || ''}`;

  const displayPicture = generateImageURL(
    user?.userInfo.shopLogoUrl || '',
    'public'
  );

  const initials = `${firstName[0] || ''}${lastName[0] || ''}`;
  const tenantUserId = `${userId}-${tenantId}`;
  const tenantType = decodedJwt.tenantType || 'COMPANY';

  return {
    ...rest,
    token,
    storeOptions,
    user: {
      ...userRest,
      authenticationType,
      biddingType,
      displayPicture,
      firstName,
      fullName,
      hasChangePasswordPermission,
      hasCreateBidsPermission,
      hasEditProfilePermission,
      hasMemberGrrPermission,
      hasPodSwitchPermission,
      hasProfilePermission,
      hasUpdateInventoryPermission,
      hasViewAccountStatementPermission,
      hasViewAssessmentImagesPermission,
      hasViewBidsPermission,
      hasViewDealsPermission,
      hasViewGrrPermission,
      hasViewNotificationsPermission,
      hasViewTransactionsPermission,
      hasDealerRegistrationPermission,
      lastName,
      initials: initials.toUpperCase(),
      isCreateTrayPermission,
      isNewTransferPermission,
      isReadPermission,
      isWritePermission,
      tenantId,
      tenantType,
      userId: tenantUserId,
    },
  } as ILoginUserData;
}
